import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, FileText, Plus, ArrowRight, Search, Activity as ActivityIcon,
  CheckCircle2, AlertTriangle, Gavel, LayoutGrid, ListTree, Radio, ExternalLink,
} from 'lucide-react';
import {
  pmApi, type Summary, type AttentionItem, type ActivityEvent, type Task, type Metrics, type PlanningSource,
} from './pmApi';
import { assignableOwnerCodes } from './sourceInfo';
import {
  StateLabel, ReasonList, EmptyState, LoadingBlock, fmtDate, fmtWhen, cleanName, cleanBlocker,
} from './components';
import DepartmentRail, { type QuickFilter } from './DepartmentRail';
import TaskList from './TaskList';
import TaskDetailPanel from './TaskDetailPanel';
import TaskDialog from './TaskDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/new-components/ui/card';
import { Button } from '../../../components/new-components/ui/button';
import { Badge } from '../../../components/new-components/ui/badge';
import { Input } from '../../../components/new-components/ui/input';
import { Sheet, SheetContent } from '../../../components/new-components/ui/sheet';

/* eMerge Operational Command Center.

   A workspace, not a report. Three panes: departments on the left (the plan
   ships as five per-department exports and that is how the business is run),
   the work in the middle, and the selected item on the right. Nothing stacks —
   each pane scrolls independently, so triage never means scrolling past four
   tables to reach the fifth.

   Built entirely on the portal's own primitives so it inherits the admin
   shell's theme rather than defining a competing one. */

/* The server caps a task list at 500 and cuts it in due-date order, before ranking by risk. */
const TASK_LIMIT = 500;

const VIEWS = [
  { key: 'work', label: 'Work', icon: <ListTree className="h-3.5 w-3.5" /> },
  { key: 'overview', label: 'Overview', icon: <LayoutGrid className="h-3.5 w-3.5" /> },
  { key: 'activity', label: 'Activity', icon: <ActivityIcon className="h-3.5 w-3.5" /> },
] as const;
type ViewKey = typeof VIEWS[number]['key'];

/** Persistent right pane on wide screens; a drawer below that. */
function useIsWide(query = '(min-width: 1280px)') {
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = (e: MediaQueryListEvent) => setWide(e.matches);
    mq.addEventListener('change', on);
    setWide(mq.matches);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return wide;
}

const ATTENTION_META: Record<AttentionItem['kind'], { label: string; accent: string }> = {
  DECISION_REQUIRED: { label: 'Decision required', accent: 'bg-primary' },
  MILESTONE_BREACH: { label: 'Milestone breach', accent: 'bg-red-500' },
  BLOCKED: { label: 'Blocked', accent: 'bg-orange-500' },
  CRITICAL_DEADLINE: { label: 'Critical deadline', accent: 'bg-amber-500' },
};

function MiniStat({
  label, value, tone = 'default', active, onClick,
}: {
  label: string; value: React.ReactNode;
  tone?: 'default' | 'ok' | 'warn' | 'risk' | 'breach';
  active?: boolean; onClick?: () => void;
}) {
  const toneClass = {
    default: 'text-[#242424] dark:text-foreground',
    ok: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    risk: 'text-orange-600 dark:text-orange-400',
    breach: 'text-red-600 dark:text-red-400',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      aria-pressed={active}
      className={`min-w-0 rounded-md border px-3 py-2 text-left transition-colors ${
        active ? 'border-primary bg-primary/5' : 'border-border'
      } ${onClick ? 'hover:bg-muted/50' : 'cursor-default'}`}
    >
      <p className={`text-lg font-semibold leading-none tabular-nums ${toneClass}`}>{value}</p>
      <p className="mt-1 truncate text-[11px] leading-tight text-muted-foreground">{label}</p>
    </button>
  );
}

export default function CommandCenter() {
  const navigate = useNavigate();
  const isWide = useIsWide();

  const [summary, setSummary] = useState<Summary | null>(null);
  const [attention, setAttention] = useState<AttentionItem[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [jiraSource, setJiraSource] = useState<PlanningSource | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);

  const [department, setDepartment] = useState<string | 'all'>('all');
  const [quick, setQuick] = useState<QuickFilter>('none');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewKey>('work');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [live, setLive] = useState(false);

  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Context — summary, attention, activity. Refreshed on demand. */
  const loadContext = useCallback(async () => {
    try {
      const [s, a, ev, o] = await Promise.all([
        pmApi.getSummary(), pmApi.getAttention(), pmApi.getActivity(40), pmApi.getOwners(),
      ]);
      if (s.success && s.data) setSummary(s.data);
      else setError(s.error || 'Could not load the command center.');
      if (a.success && a.data) setAttention(a.data);
      if (ev.success && ev.data) setActivity(ev.data);
      if (o.success && o.data) setOwners(assignableOwnerCodes(o.data));
    } catch {
      setError('Could not reach the command center service.');
    } finally {
      setLoading(false);
    }
    // Source freshness is secondary: a failure here never blanks the page.
    try {
      const src = await pmApi.getSources();
      if (src.success && src.data) setJiraSource(src.data.find((x) => x.source === 'jira') || null);
    } catch { /* the header simply omits the Jira line */ }
  }, []);

  /* Work list — refetched whenever the department or a filter changes. */
  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const res = await pmApi.getTasks({
        workstreamId: department === 'all' ? undefined : department,
        overdue: quick === 'overdue' || undefined,
        blocked: quick === 'blocked' || undefined,
        milestones: quick === 'milestones' || undefined,
        criticalPath: quick === 'criticalPath' || undefined,
        q: search || undefined,
        limit: TASK_LIMIT,
      });
      if (res.success && res.data) setTasks(res.data);
    } finally {
      setTasksLoading(false);
    }
  }, [department, quick, search]);

  useEffect(() => { loadContext(); }, [loadContext]);
  useEffect(() => {
    const t = setTimeout(loadTasks, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadTasks, search]);

  /* Activity monitor: poll only while the Activity view is open and live is on. */
  useEffect(() => {
    if (!live || view !== 'activity') return;
    const id = setInterval(() => {
      pmApi.getActivity(40).then((r) => { if (r.success && r.data) setActivity(r.data); });
    }, 20000);
    return () => clearInterval(id);
  }, [live, view]);

  const refreshAll = () => { setLoading(true); loadContext(); loadTasks(); };

  /* Pull Jira now (inbound only). The server refuses if Jira is not
     configured or a sync is already running, and says why. */
  const syncJira = async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res = await pmApi.syncJira();
      if (res.success && res.data) {
        const d = res.data;
        setSyncNote(`Jira synced: ${d.created ?? 0} new, ${d.updated ?? 0} changed, ${d.unchanged ?? 0} unchanged.`);
        refreshAll();
      } else {
        setSyncNote(res.message || res.error || 'The Jira sync did not run.');
      }
    } catch {
      setSyncNote('The Jira sync did not run.');
    } finally {
      setSyncing(false);
    }
  };
  const jiraSynced = jiraSource?.lastCompleteBatch?.finishedAt || null;

  /* "No next action" has no server-side filter — it is a property of the
     computed risk, so it is applied here against the fetched list. */
  const visibleTasks = useMemo(
    () => (quick === 'noNextAction'
      ? tasks.filter((t) => !t.nextAction && t.statusNormalized !== 'done')
      : tasks),
    [tasks, quick]
  );

  const dept = summary?.workstreams.find((w) => w.id === department) || null;
  const m: Metrics | undefined = summary?.metrics;

  const scopedActivity = useMemo(
    () => (dept ? activity.filter((e) => e.workstream_name === dept.name) : activity),
    [activity, dept]
  );
  const scopedAttention = useMemo(
    () => (dept ? attention.filter((i) => i.workstream === dept.name) : attention),
    [attention, dept]
  );

  const openAttention = (i: AttentionItem) => {
    if (i.kind === 'DECISION_REQUIRED') return;
    setSelectedId(i.id);
    setView('work');
  };

  if (loading && !summary) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-display font-semibold text-[#242424] dark:text-foreground sm:text-2xl">Operational Command</h1>
        <Card className="border-border"><CardContent className="p-0"><LoadingBlock label="Assembling the operating picture" /></CardContent></Card>
      </div>
    );
  }

  const detail = selectedId ? (
    <TaskDetailPanel
      taskId={selectedId}
      onClose={() => setSelectedId(null)}
      onChanged={() => { loadTasks(); loadContext(); }}
    />
  ) : null;

  return (
    <div className="min-w-0 space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-display font-semibold text-[#242424] dark:text-foreground sm:text-2xl">
            Operational Command
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {summary?.freshness
              ? <>Plan imported {fmtWhen(summary.freshness.importedAt)} · {summary.freshness.rows} items · {m?.workstreams} departments</>
              : 'Executive operating view over the company master plan'}
            {jiraSynced ? <> · Jira synced {fmtWhen(jiraSynced)} ({jiraSource?.tasks ?? 0} items, read-only)</> : null}
          </p>
          {syncNote ? <p className="mt-1 text-xs text-muted-foreground" role="status">{syncNote}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={refreshAll} className="gap-1">
            <RefreshCw className={`h-3.5 w-3.5 ${tasksLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {jiraSource?.config?.configured ? (
            <Button variant="outline" size="sm" onClick={syncJira} disabled={syncing} className="gap-1">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync Jira
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Add to plan
          </Button>
          <Button size="sm" onClick={() => navigate('/admin/command/weekly-review')} className="gap-2">
            <FileText className="h-4 w-4" /> Weekly Review
          </Button>
        </div>
      </div>

      {error ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
          {error}
        </p>
      ) : null}

      {/* Compact, clickable metric strip */}
      {m ? (
        <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
          <MiniStat label="Departments at risk" value={m.atRiskWorkstreams + m.blockedWorkstreams} tone={m.atRiskWorkstreams + m.blockedWorkstreams ? 'risk' : 'ok'} />
          <MiniStat label="Overdue critical" value={m.overdueCritical} tone="breach" active={quick === 'overdue'} onClick={() => { setQuick(quick === 'overdue' ? 'none' : 'overdue'); setView('work'); }} />
          <MiniStat label="Blocked" value={m.blocked} tone={m.blocked ? 'risk' : 'ok'} active={quick === 'blocked'} onClick={() => { setQuick(quick === 'blocked' ? 'none' : 'blocked'); setView('work'); }} />
          <MiniStat label="Milestones breached" value={m.milestonesBreached} tone={m.milestonesBreached ? 'breach' : 'ok'} active={quick === 'milestones'} onClick={() => { setQuick(quick === 'milestones' ? 'none' : 'milestones'); setView('work'); }} />
          <MiniStat label="Decisions required" value={m.decisionsRequired} tone={m.decisionsRequired ? 'warn' : 'ok'} />
          <MiniStat label="No next action" value={m.missingNextAction} tone={m.missingNextAction ? 'risk' : 'ok'} active={quick === 'noNextAction'} onClick={() => { setQuick(quick === 'noNextAction' ? 'none' : 'noNextAction'); setView('work'); }} />
        </div>
      ) : null}

      {/* Workspace: rail | content | detail */}
      <div className={`grid gap-4 ${selectedId && isWide ? 'xl:grid-cols-[224px_minmax(0,1fr)_380px]' : 'lg:grid-cols-[224px_minmax(0,1fr)]'}`}>
        {/* Rail */}
        <aside className="lg:sticky lg:top-4 lg:self-start">
          <Card className="border-border">
            <CardContent className="p-3">
              <DepartmentRail
                workstreams={summary?.workstreams || []}
                metrics={m}
                selected={department}
                onSelect={(id) => { setDepartment(id); setSelectedId(null); }}
                quick={quick}
                onQuick={(qf) => { setQuick(qf); setView('work'); }}
              />
            </CardContent>
          </Card>
        </aside>

        {/* Content */}
        <main className="min-w-0 space-y-3">
          {/* Department header + view switcher */}
          <Card className="border-border">
            <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="truncate text-sm font-semibold text-[#242424] dark:text-foreground">
                      {dept ? dept.name : 'All departments'}
                    </h2>
                    {dept ? <StateLabel state={dept.health} /> : null}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {dept
                      ? <>{dept.executiveOwner || 'Owner unassigned'} · {dept.stats.open} open · {dept.stats.overdue} overdue · {dept.stats.blocked} blocked</>
                      : <>{m?.tasksOpen} open items across {m?.workstreams} departments</>}
                  </p>
                </div>
                {dept ? (
                  <Button variant="ghost" size="sm" className="shrink-0 gap-1" onClick={() => navigate(`/admin/command/workstream/${dept.id}`)}>
                    <ExternalLink className="h-3.5 w-3.5" /> Full view
                  </Button>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-0 flex-1 sm:w-48 sm:flex-none">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className="h-8 pl-8 text-sm" />
                </div>
                <div className="flex rounded-md border border-border p-0.5">
                  {VIEWS.map((v) => (
                    <Button
                      key={v.key}
                      variant={view === v.key ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7 gap-1.5 px-2.5 text-xs"
                      onClick={() => setView(v.key)}
                    >
                      {v.icon}<span className="hidden sm:inline">{v.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ── Work ── */}
          {view === 'work' ? (
            <Card className="border-border">
              <CardContent className="p-0">
                {tasks.length >= TASK_LIMIT ? (
                  <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground" role="status">
                    Showing the first {TASK_LIMIT} items by due date; undated items may be cut off. Pick a department or search to see the rest.
                  </p>
                ) : null}
                {tasksLoading && !visibleTasks.length ? <LoadingBlock label="Loading work" /> : (
                  <TaskList
                    tasks={visibleTasks}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    groupByPhase={Boolean(dept)}
                  />
                )}
              </CardContent>
            </Card>
          ) : null}

          {/* ── Overview ── */}
          {view === 'overview' ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Needs a leadership move</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {scopedAttention.length ? (
                    <div className="divide-y divide-border">
                      {scopedAttention.map((i) => (
                        <button
                          key={`${i.kind}-${i.id}`}
                          type="button"
                          onClick={() => openAttention(i)}
                          className="flex w-full gap-2.5 p-3 text-left transition-colors hover:bg-muted/50"
                        >
                          <span className={`mt-1 w-1 shrink-0 rounded-full ${ATTENTION_META[i.kind].accent}`} />
                          <span className="min-w-0 flex-1">
                            <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {ATTENTION_META[i.kind].label}
                            </span>
                            <span className="mt-0.5 block text-sm font-medium text-[#242424] dark:text-foreground">{cleanName(i.title)}</span>
                            <span className="mt-0.5 block text-xs text-muted-foreground">
                              {i.owner ? `${i.owner} · ` : ''}{i.impact ? cleanBlocker(i.impact) : ''}
                            </span>
                          </span>
                          <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <EmptyState icon={<CheckCircle2 className="h-7 w-7" />} title="Nothing waiting on leadership" />
                  )}
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">
                    {dept ? 'Why this department is ' : 'Critical path — September first patient'}
                    {dept ? <span className="font-normal">{dept.health}</span> : null}
                  </CardTitle>
                </CardHeader>
                <CardContent className={dept ? 'pt-0' : 'p-0'}>
                  {dept ? (
                    <ReasonList reasons={dept.healthReasons} />
                  ) : (
                    <div className="divide-y divide-border">
                      {summary?.criticalPath.links.map((l) => (
                        <div key={l.id} className="flex items-start gap-3 p-3">
                          <span
                            className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                              l.isDone ? 'bg-emerald-500'
                                : l.confidence === 'BREACHED' ? 'bg-red-500'
                                  : l.confidence === 'UNLIKELY' ? 'bg-orange-400' : 'bg-muted-foreground/40'
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#242424] dark:text-foreground">{l.cleanName}</p>
                            <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                              {l.ownerRole ? `${l.ownerRole} · ` : ''}{fmtDate(l.dueDate)} · {l.percentComplete}%
                              {l.daysRemaining !== null && l.daysRemaining < 0 ? (
                                <span className="text-red-600 dark:text-red-400"> · {Math.abs(l.daysRemaining)}d late</span>
                              ) : null}
                            </p>
                            <ReasonList reasons={l.reasons} max={1} className="mt-1" />
                          </div>
                          <StateLabel state={l.confidence} className="shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Department board — only when looking across all of them */}
              {!dept ? (
                <Card className="border-border lg:col-span-2">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Department health</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-3">
                      {summary?.workstreams.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => { setDepartment(w.id); setView('work'); }}
                          className="bg-card p-3 text-left transition-colors hover:bg-muted/50"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="min-w-0 truncate text-sm font-medium text-[#242424] dark:text-foreground">{w.name}</span>
                            <StateLabel state={w.health} className="shrink-0" />
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {w.executiveOwner || 'Unassigned'} · {w.stats.open} open · {w.stats.overdue} overdue
                          </p>
                          {w.criticalMilestone ? (
                            <p className="mt-1.5 truncate text-xs text-foreground/75">
                              Next: {w.criticalMilestone.name}
                              <span className="tabular-nums text-muted-foreground"> · {fmtDate(w.criticalMilestone.dueDate)}</span>
                            </p>
                          ) : null}
                          <ReasonList reasons={w.healthReasons} max={1} className="mt-1.5" />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          ) : null}

          {/* ── Activity monitor ── */}
          {view === 'activity' ? (
            <Card className="border-border">
              <CardHeader className="flex-row items-center justify-between gap-3 pb-2">
                <div>
                  <CardTitle className="text-sm">Activity monitor</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {dept ? `${dept.name} · ` : ''}meaningful state changes only
                  </p>
                </div>
                <Button
                  variant={live ? 'secondary' : 'outline'}
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setLive((v) => !v)}
                  aria-pressed={live}
                >
                  <Radio className={`h-3.5 w-3.5 ${live ? 'text-emerald-500' : ''}`} />
                  {live ? 'Live · 20s' : 'Go live'}
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {scopedActivity.length ? (
                  <div className="divide-y divide-border">
                    {scopedActivity.map((e) => (
                      <div key={e.id} className="flex items-baseline gap-3 px-3 py-2.5">
                        <span className="w-16 shrink-0 text-xs text-muted-foreground">{fmtWhen(e.changed_at)}</span>
                        <span className="min-w-0 flex-1 text-sm">
                          <span className="font-medium text-[#242424] dark:text-foreground">{cleanName(e.task_name || 'Item')}</span>
                          <span className="text-muted-foreground">
                            {' — '}
                            {e.change_kind === 'status_change'
                              ? <>status {e.before_value} → <b className="font-medium text-foreground/80">{e.after_value}</b></>
                              : e.change_kind === 'date_slip'
                                ? <>{e.field.replace(/_/g, ' ')} {e.before_value} → <b className="font-medium text-foreground/80">{e.after_value}</b></>
                                : <>{e.field.replace(/_/g, ' ')} set to <b className="font-medium text-foreground/80">{e.after_value || '—'}</b></>}
                          </span>
                        </span>
                        <Badge variant="outline" className="shrink-0 text-[10px] font-normal">
                          {e.source === 'human' ? 'Entered here' : 'Import'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={<ActivityIcon className="h-7 w-7" />} title="No state changes recorded yet">
                    Movement appears here when the plan is re-imported or an item is edited.
                    Every change records who made it, what it was, and what it became.
                  </EmptyState>
                )}
              </CardContent>
            </Card>
          ) : null}
        </main>

        {/* Detail — persistent column on wide screens */}
        {selectedId && isWide ? (
          <aside className="xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:self-start">
            <Card className="h-full border-border xl:max-h-[calc(100vh-2rem)]">
              <CardContent className="h-full p-0">{detail}</CardContent>
            </Card>
          </aside>
        ) : null}
      </div>

      {/* Detail — drawer on narrow screens */}
      {!isWide ? (
        <Sheet open={Boolean(selectedId)} onOpenChange={(o) => { if (!o) setSelectedId(null); }}>
          <SheetContent side="right" className="w-full p-0 sm:max-w-md">{detail}</SheetContent>
        </Sheet>
      ) : null}

      <TaskDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        workstreams={summary?.workstreams || []}
        owners={owners}
        defaultWorkstreamId={department === 'all' ? undefined : department}
        onSaved={() => { loadTasks(); loadContext(); }}
      />
    </div>
  );
}
